import { saveAs } from "file-saver";
import { calculateRequirements } from "./calculationUtils";
import { formatCpu, formatBytes } from "./productUtils";
import { AS_VERSION_OPTIONS } from "../constants/calculatorConstants";
import { jsonToQueryParamData } from "./sharableCalculatorUtils";

const RECOMMENDATION_SELECTOR = '[data-export-target="recommendation"]';

// Capture the rendered "Minimal Recommended Configuration" widget as a PNG.
// html2canvas derives the capture region from the live element's bounding box,
// so if any Material UI Accordion inside is collapsed (height: 0) the resulting
// image is clipped to just the header. We force-expand collapsed MuiCollapse
// panels synchronously, let html2canvas clone the DOM (its DocumentCloner runs
// synchronously before the first internal await), then restore the live DOM —
// all in one synchronous block, so the browser never paints the expanded state.
const captureRecommendationImage = async (): Promise<{
    dataUrl: string;
    width: number;
    height: number;
} | null> => {
    const el = document.querySelector(RECOMMENDATION_SELECTOR) as HTMLElement | null;
    if (!el) return null;

    let html2canvas: typeof import("html2canvas").default;
    try {
        ({ default: html2canvas } = await import("html2canvas"));
    } catch (err) {
        console.warn("Failed to load html2canvas", err);
        return null;
    }

    const collapsedPanels: Array<{ el: HTMLElement; cssText: string; className: string }> = [];
    el.querySelectorAll<HTMLElement>(".MuiCollapse-hidden").forEach((c) => {
        collapsedPanels.push({ el: c, cssText: c.style.cssText, className: c.className });
        c.classList.remove("MuiCollapse-hidden");
        c.style.height = "auto";
        c.style.minHeight = "0";
        c.style.visibility = "visible";
    });

    const scale = 2;
    let canvasPromise: Promise<HTMLCanvasElement>;
    try {
        canvasPromise = html2canvas(el, {
            scale,
            backgroundColor: "#ffffff",
            useCORS: true,
            logging: false,
        });
    } finally {
        for (const { el: c, cssText, className } of collapsedPanels) {
            c.style.cssText = cssText;
            c.className = className;
        }
    }

    try {
        const canvas = await canvasPromise;
        return {
            dataUrl: canvas.toDataURL("image/png"),
            width: Math.round(canvas.width / scale),
            height: Math.round(canvas.height / scale),
        };
    } catch (err) {
        console.warn("Failed to capture recommendation widget for excel export", err);
        return null;
    }
};

const DEPLOYMENT_TYPE_LABELS: Record<string, string> = {
    "eval": "Automation Suite - Single Node",
    "lite": "Automation Suite - Multi Node Lite",
    "ha": "Automation Suite - Multi Node",
    "air-aks": "Automation Suite - AKS",
    "air-eks": "Automation Suite - EKS",
    "air-openshift": "Automation Suite - Openshift",
};

const PROFILE_LABELS: Record<string, string> = {
    "default": "Default",
    "lite": "Lite",
    "ha": "HA",
};

const yesNo = (v: boolean | undefined): string => (v ? "Yes" : "No");

const getProfileLabel = (formData: CalculatorFormDataType): string => {
    const hlp = formData.highLevelParameters;
    switch (hlp.deploymentType) {
        case "air-aks":
            return PROFILE_LABELS[hlp.aksProfile] || hlp.aksProfile || "Default";
        case "air-eks":
            return PROFILE_LABELS[hlp.eksProfile] || hlp.eksProfile || "Default";
        case "air-openshift":
            return PROFILE_LABELS[hlp.openshiftProfile] || hlp.openshiftProfile || "Default";
        default:
            return "N/A";
    }
};

const formatPropertyValue = (value: any): string => {
    if (value === null || value === undefined) return "";
    if (typeof value === "boolean") return yesNo(value);
    return String(value);
};

// camelCase / snake_case → Title Case
const humanizeKey = (key: string): string =>
    key
        .replace(/_/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (c) => c.toUpperCase());

const formatProperties = (props: Record<string, any> | undefined): string => {
    if (!props) return "";
    return Object.entries(props)
        .filter(([, v]) => v !== "" && v !== null && v !== undefined)
        .map(([k, v]) => `${humanizeKey(k)}: ${formatPropertyValue(v)}`)
        .join("\n");
};

const nodeDrivesText = (drives: string[] | undefined): string =>
    (drives || []).filter(Boolean).join("\n");

const stripUnit = (raw: string | undefined, unit: string): string | number => {
    if (!raw) return "";
    const cleaned = raw
        .replace(new RegExp(`\\s*${unit}`, "g"), "")
        .replace(/\s+/g, " ")
        .trim();
    if (/^-?\d+(\.\d+)?$/.test(cleaned)) return Number(cleaned);
    return cleaned;
};

export const exportCalculatorToExcel = async (formData: CalculatorFormDataType): Promise<void> => {
    const ExcelJS = await import("exceljs");
    const wb = new ExcelJS.Workbook();
    const recommendation = calculateRequirements(formData);
    // The flat sizing fields (`nodes`, `sqlNodes`, `nfsNode`, `totalCpu`, ...)
    // moved to `SiteResult` under `primary` when A/P support landed. The Excel
    // export remains single-site (primary) for now; A/P-aware export with a
    // Secondary sheet/section can be a follow-up.
    const primary = recommendation.primary || ({} as SiteResult);
    const hlp = formData.highLevelParameters;

    // Create Sizing sheet
    const sizingSheet = wb.addWorksheet("Sizing");

    // Title
    let row = 1;
    let titleRow = sizingSheet.getRow(row);
    titleRow.values = ["Automation Suite Install Sizing Calculator"];
    titleRow.font = { bold: true, size: 14, color: { argb: "FFFFFFFF" } };
    titleRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1F4E78" } };
    titleRow.alignment = { vertical: "middle", horizontal: "center" };
    sizingSheet.mergeCells(`A${row}:F${row}`);
    sizingSheet.getRow(row).height = 24;
    row++;

    // Generated timestamp
    let subRow = sizingSheet.getRow(row);
    subRow.values = [`Generated: ${new Date().toLocaleString()}`];
    subRow.font = { italic: true, size: 10, color: { argb: "FF7F7F7F" } };
    subRow.alignment = { vertical: "middle", horizontal: "left" };
    sizingSheet.mergeCells(`A${row}:F${row}`);
    row++;

    row++; // blank

    // Shareable URL
    const shareableUrl = `${window.location.href.split("?")[0]}?${jsonToQueryParamData(formData)}`;
    let noteRow = sizingSheet.getRow(row);
    noteRow.values = [
        "To re-open this configuration in the calculator, copy the URL below and paste it into your browser. Note: this URL is a snapshot — calculator defaults or available products may change later.",
    ];
    noteRow.font = { italic: true, size: 9, color: { argb: "FF7F7F7F" } };
    noteRow.alignment = { vertical: "top", horizontal: "left", wrapText: true };
    sizingSheet.mergeCells(`A${row}:F${row}`);
    row++;

    let urlRow = sizingSheet.getRow(row);
    urlRow.values = [shareableUrl];
    urlRow.font = { size: 9, color: { argb: "FF595959" } };
    urlRow.alignment = { vertical: "middle", horizontal: "left", wrapText: false };
    sizingSheet.mergeCells(`A${row}:F${row}`);
    row++;

    row++; // blank

    // Deployment Configuration section
    let sectionRow = sizingSheet.getRow(row);
    sectionRow.values = ["Deployment Configuration"];
    sectionRow.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
    sectionRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF305496" } };
    sectionRow.alignment = { vertical: "middle", horizontal: "center" };
    sizingSheet.mergeCells(`A${row}:B${row}`);
    row++;

    // Headers for config
    let headerRow = sizingSheet.getRow(row);
    headerRow.values = ["Field", "Value"];
    headerRow.font = { bold: true, color: { argb: "FF1F4E78" } };
    headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD9E1F2" } };
    headerRow.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    for (let c = 1; c <= 2; c++) {
        headerRow.getCell(c).border = {
            top: { style: "thin", color: { argb: "FFD9D9D9" } },
            bottom: { style: "thin", color: { argb: "FF8EA9DB" } },
            left: { style: "thin", color: { argb: "FFD9D9D9" } },
            right: { style: "thin", color: { argb: "FFD9D9D9" } },
        };
    }
    row++;

    const config: [string, string | number][] = [
        ["AS Flavour", DEPLOYMENT_TYPE_LABELS[hlp.deploymentType] || hlp.deploymentType],
        ["Profile", getProfileLabel(formData)],
        ["Version", AS_VERSION_OPTIONS.find((opt) => opt.value === hlp.version)?.label || hlp.version || ""],
        ["Host", hlp.host || ""],
        ["Air Gapped", yesNo(hlp.airGapped)],
        ["Backup Enabled", yesNo(hlp.backupEnabled)],
        ["Multi-zonal", yesNo(hlp.isMultizoanl)],
        ["External S3", yesNo(hlp.externalS3)],
        ["Fault Tolerance", hlp.faultCount ?? 0],
        ["CPU per Node (vCPU)", hlp.coreCount ?? 0],
        ["RAM per Node (GiB)", hlp.ramCount ?? 0],
    ];

    for (const [k, v] of config) {
        let r = sizingSheet.getRow(row);
        r.values = [k, v];
        r.getCell(1).font = { bold: true };
        r.getCell(1).alignment = { vertical: "top", horizontal: "left", wrapText: true };
        r.getCell(2).alignment = { vertical: "top", horizontal: "left", wrapText: true };
        for (let c = 1; c <= 2; c++) {
            r.getCell(c).border = {
                top: { style: "thin", color: { argb: "FFD9D9D9" } },
                bottom: { style: "thin", color: { argb: "FFD9D9D9" } },
                left: { style: "thin", color: { argb: "FFD9D9D9" } },
                right: { style: "thin", color: { argb: "FFD9D9D9" } },
            };
        }
        row++;
    }

    row++;

    // Selected Products section
    sectionRow = sizingSheet.getRow(row);
    sectionRow.values = ["Selected Products"];
    sectionRow.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
    sectionRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF305496" } };
    sectionRow.alignment = { vertical: "middle", horizontal: "center" };
    sizingSheet.mergeCells(`A${row}:F${row}`);
    row++;

    headerRow = sizingSheet.getRow(row);
    headerRow.values = ["Product", "Properties"];
    headerRow.font = { bold: true, color: { argb: "FF1F4E78" } };
    headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD9E1F2" } };
    headerRow.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    for (let c = 1; c <= 6; c++) {
        headerRow.getCell(c).border = {
            top: { style: "thin", color: { argb: "FFD9D9D9" } },
            bottom: { style: "thin", color: { argb: "FF8EA9DB" } },
            left: { style: "thin", color: { argb: "FFD9D9D9" } },
            right: { style: "thin", color: { argb: "FFD9D9D9" } },
        };
    }
    row++;

    for (const product of formData.products) {
        let r = sizingSheet.getRow(row);
        r.values = [product.name, formatProperties(product.properties)];
        r.getCell(1).font = { bold: true };
        r.getCell(1).alignment = { vertical: "top", horizontal: "left", wrapText: true };
        r.getCell(2).alignment = { vertical: "top", horizontal: "left", wrapText: true };
        sizingSheet.mergeCells(`B${row}:F${row}`);
        for (let c = 1; c <= 6; c++) {
            r.getCell(c).border = {
                top: { style: "thin", color: { argb: "FFD9D9D9" } },
                bottom: { style: "thin", color: { argb: "FFD9D9D9" } },
                left: { style: "thin", color: { argb: "FFD9D9D9" } },
                right: { style: "thin", color: { argb: "FFD9D9D9" } },
            };
        }
        row++;
    }

    row++;

    // Node Requirements section
    sectionRow = sizingSheet.getRow(row);
    sectionRow.values = ["Node Requirements"];
    sectionRow.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
    sectionRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF305496" } };
    sectionRow.alignment = { vertical: "middle", horizontal: "center" };
    sizingSheet.mergeCells(`A${row}:F${row}`);
    row++;

    headerRow = sizingSheet.getRow(row);
    headerRow.values = ["Node Type", "Count", "CPU (vCPU)", "RAM (GiB)", "GPU", "Drives"];
    headerRow.font = { bold: true, color: { argb: "FF1F4E78" } };
    headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD9E1F2" } };
    headerRow.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    for (let c = 1; c <= 6; c++) {
        headerRow.getCell(c).border = {
            top: { style: "thin", color: { argb: "FFD9D9D9" } },
            bottom: { style: "thin", color: { argb: "FF8EA9DB" } },
            left: { style: "thin", color: { argb: "FFD9D9D9" } },
            right: { style: "thin", color: { argb: "FFD9D9D9" } },
        };
    }
    row++;

    for (const node of primary.nodes || []) {
        let r = sizingSheet.getRow(row);
        r.values = [
            node.nodeType || "",
            node.nodeCount || "",
            stripUnit(node.cpu, "vCPU"),
            stripUnit(node.ram, "GiB"),
            node.gpu || "",
            nodeDrivesText(node.drives),
        ];
        r.alignment = { vertical: "top", horizontal: "center", wrapText: true };
        for (let c = 1; c <= 6; c++) {
            r.getCell(c).border = {
                top: { style: "thin", color: { argb: "FFD9D9D9" } },
                bottom: { style: "thin", color: { argb: "FFD9D9D9" } },
                left: { style: "thin", color: { argb: "FFD9D9D9" } },
                right: { style: "thin", color: { argb: "FFD9D9D9" } },
            };
        }
        row++;
    }

    // SQL Nodes
    if (primary.sqlNodes && primary.sqlNodes.length > 0) {
        row++;
        sectionRow = sizingSheet.getRow(row);
        sectionRow.values = ["SQL Nodes"];
        sectionRow.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
        sectionRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF305496" } };
        sectionRow.alignment = { vertical: "middle", horizontal: "center" };
        sizingSheet.mergeCells(`A${row}:E${row}`);
        row++;

        headerRow = sizingSheet.getRow(row);
        headerRow.values = ["Node Type", "Count", "CPU (vCPU)", "RAM (GiB)", "Drives"];
        headerRow.font = { bold: true, color: { argb: "FF1F4E78" } };
        headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD9E1F2" } };
        headerRow.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
        for (let c = 1; c <= 5; c++) {
            headerRow.getCell(c).border = {
                top: { style: "thin", color: { argb: "FFD9D9D9" } },
                bottom: { style: "thin", color: { argb: "FF8EA9DB" } },
                left: { style: "thin", color: { argb: "FFD9D9D9" } },
                right: { style: "thin", color: { argb: "FFD9D9D9" } },
            };
        }
        row++;

        for (const node of primary.sqlNodes) {
            let r = sizingSheet.getRow(row);
            r.values = [
                node.nodeType || "",
                node.nodeCount || "",
                stripUnit(node.cpu, "vCPU"),
                stripUnit(node.ram, "GiB"),
                nodeDrivesText(node.drives),
            ];
            r.alignment = { vertical: "top", horizontal: "center", wrapText: true };
            for (let c = 1; c <= 5; c++) {
                r.getCell(c).border = {
                    top: { style: "thin", color: { argb: "FFD9D9D9" } },
                    bottom: { style: "thin", color: { argb: "FFD9D9D9" } },
                    left: { style: "thin", color: { argb: "FFD9D9D9" } },
                    right: { style: "thin", color: { argb: "FFD9D9D9" } },
                };
            }
            row++;
        }
    }

    // NFS Node
    if (["eval", "ha", "lite"].includes(hlp.deploymentType) && primary.nfsNode) {
        row++;
        sectionRow = sizingSheet.getRow(row);
        sectionRow.values = ["NFS Node"];
        sectionRow.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
        sectionRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF305496" } };
        sectionRow.alignment = { vertical: "middle", horizontal: "center" };
        sizingSheet.mergeCells(`A${row}:E${row}`);
        row++;

        headerRow = sizingSheet.getRow(row);
        headerRow.values = ["Node Type", "Count", "CPU (vCPU)", "RAM (GiB)", "Drives"];
        headerRow.font = { bold: true, color: { argb: "FF1F4E78" } };
        headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD9E1F2" } };
        headerRow.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
        for (let c = 1; c <= 5; c++) {
            headerRow.getCell(c).border = {
                top: { style: "thin", color: { argb: "FFD9D9D9" } },
                bottom: { style: "thin", color: { argb: "FF8EA9DB" } },
                left: { style: "thin", color: { argb: "FFD9D9D9" } },
                right: { style: "thin", color: { argb: "FFD9D9D9" } },
            };
        }
        row++;

        let r = sizingSheet.getRow(row);
        r.values = [
            primary.nfsNode.nodeType || "",
            primary.nfsNode.nodeCount || "",
            stripUnit(primary.nfsNode.cpu, "vCPU"),
            stripUnit(primary.nfsNode.ram, "GiB"),
            nodeDrivesText(primary.nfsNode.drives),
        ];
        r.alignment = { vertical: "top", horizontal: "center", wrapText: true };
        for (let c = 1; c <= 5; c++) {
            r.getCell(c).border = {
                top: { style: "thin", color: { argb: "FFD9D9D9" } },
                bottom: { style: "thin", color: { argb: "FFD9D9D9" } },
                left: { style: "thin", color: { argb: "FFD9D9D9" } },
                right: { style: "thin", color: { argb: "FFD9D9D9" } },
            };
        }
        row++;
    }

    row++;

    // Totals section
    sectionRow = sizingSheet.getRow(row);
    sectionRow.values = ["Totals"];
    sectionRow.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
    sectionRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF305496" } };
    sectionRow.alignment = { vertical: "middle", horizontal: "center" };
    sizingSheet.mergeCells(`A${row}:B${row}`);
    row++;

    headerRow = sizingSheet.getRow(row);
    headerRow.values = ["Field", "Value"];
    headerRow.font = { bold: true, color: { argb: "FF1F4E78" } };
    headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD9E1F2" } };
    headerRow.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    for (let c = 1; c <= 2; c++) {
        headerRow.getCell(c).border = {
            top: { style: "thin", color: { argb: "FFD9D9D9" } },
            bottom: { style: "thin", color: { argb: "FF8EA9DB" } },
            left: { style: "thin", color: { argb: "FFD9D9D9" } },
            right: { style: "thin", color: { argb: "FFD9D9D9" } },
        };
    }
    row++;

    const totalCpu = primary.totalCpu ?? 0;
    const totalRamMiB = primary.totalRam ?? 0;
    const bufferedCpu = primary.bufferedCpu ?? 0;
    const bufferedRamMiB = primary.bufferedRam ?? 0;

    let r = sizingSheet.getRow(row);
    r.values = ["Total CPU", formatCpu(totalCpu)];
    r.getCell(1).font = { bold: true };
    r.getCell(2).font = { bold: true };
    for (let c = 1; c <= 2; c++) {
        r.getCell(c).border = {
            top: { style: "thin", color: { argb: "FFD9D9D9" } },
            bottom: { style: "thin", color: { argb: "FFD9D9D9" } },
            left: { style: "thin", color: { argb: "FFD9D9D9" } },
            right: { style: "thin", color: { argb: "FFD9D9D9" } },
        };
    }
    row++;

    r = sizingSheet.getRow(row);
    r.values = ["Total RAM", formatBytes(totalRamMiB * 1024 * 1024)];
    r.getCell(1).font = { bold: true };
    r.getCell(2).font = { bold: true };
    for (let c = 1; c <= 2; c++) {
        r.getCell(c).border = {
            top: { style: "thin", color: { argb: "FFD9D9D9" } },
            bottom: { style: "thin", color: { argb: "FFD9D9D9" } },
            left: { style: "thin", color: { argb: "FFD9D9D9" } },
            right: { style: "thin", color: { argb: "FFD9D9D9" } },
        };
    }
    row++;

    row++;

    r = sizingSheet.getRow(row);
    r.values = ["Total Requirement CPU", formatCpu(bufferedCpu)];
    r.getCell(1).font = { bold: true };
    r.getCell(2).font = { bold: true };
    for (let c = 1; c <= 2; c++) {
        r.getCell(c).border = {
            top: { style: "thin", color: { argb: "FFD9D9D9" } },
            bottom: { style: "thin", color: { argb: "FFD9D9D9" } },
            left: { style: "thin", color: { argb: "FFD9D9D9" } },
            right: { style: "thin", color: { argb: "FFD9D9D9" } },
        };
    }
    row++;

    r = sizingSheet.getRow(row);
    r.values = ["Total Requirement RAM", formatBytes(bufferedRamMiB * 1024 * 1024)];
    r.getCell(1).font = { bold: true };
    r.getCell(2).font = { bold: true };
    for (let c = 1; c <= 2; c++) {
        r.getCell(c).border = {
            top: { style: "thin", color: { argb: "FFD9D9D9" } },
            bottom: { style: "thin", color: { argb: "FFD9D9D9" } },
            left: { style: "thin", color: { argb: "FFD9D9D9" } },
            right: { style: "thin", color: { argb: "FFD9D9D9" } },
        };
    }

    // Set column widths
    sizingSheet.columns = [
        { width: 34 },
        { width: 22 },
        { width: 18 },
        { width: 18 },
        { width: 14 },
        { width: 32 },
    ];

    // Freeze panes
    sizingSheet.views = [{ state: "frozen", ySplit: 2, xSplit: 0, topLeftCell: "A3" }];

    // Add Details sheet (if debug data exists)
    if (primary.debug && primary.debug.length > 0) {
        const detailsSheet = wb.addWorksheet("Details");
        const NUM_COLS = 5;

        // Parse a markdown debug line into { text, indent column, level }. Strips `**bold**`
        // markers, maps heading depth and bullet nesting to an indent column, and tags the
        // level so each row can pick a font style and trigger spacer rows.
        type DetailLevel = "h2" | "h3" | "h4" | "bullet" | "plain";
        type DetailItem = { text: string; indent: number; level: DetailLevel };
        const parseDetailLine = (raw: string): DetailItem | null => {
            if (raw == null) return null;
            const stripped = raw.replace(/\*\*/g, "");
            const hMatch = stripped.match(/^(#{2,6})\s+(.*)$/);
            if (hMatch) {
                const hLevel = hMatch[1].length;
                if (hLevel === 2) return { text: hMatch[2].trim(), indent: 0, level: "h2" };
                if (hLevel === 3) return { text: hMatch[2].trim(), indent: 1, level: "h3" };
                return { text: hMatch[2].trim(), indent: 2, level: "h4" };
            }
            const bMatch = stripped.match(/^(\s*)\*\s+(.*)$/);
            if (bMatch) {
                const nested = Math.floor(bMatch[1].length / 2);
                return { text: "• " + bMatch[2], indent: Math.min(2 + nested, 3), level: "bullet" };
            }
            if (stripped.trim() === "") return null;
            return { text: stripped, indent: 2, level: "plain" };
        };

        const applyDetailStyle = (cell: any, level: DetailLevel) => {
            switch (level) {
                case "h2":
                    cell.font = { bold: true, size: 14, color: { argb: "FF1F4E78" } };
                    cell.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
                    break;
                case "h3":
                    cell.font = { bold: true, size: 12, color: { argb: "FF305496" } };
                    cell.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
                    break;
                case "h4":
                    cell.font = { bold: true, size: 11 };
                    cell.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
                    break;
                default:
                    cell.font = { size: 11 };
                    cell.alignment = { vertical: "top", horizontal: "left", wrapText: true };
            }
        };

        let detailRow = 1;
        const titleRow = detailsSheet.getRow(detailRow);
        titleRow.values = ["Calculation Details"];
        titleRow.font = { bold: true, size: 14, color: { argb: "FFFFFFFF" } };
        titleRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1F4E78" } };
        titleRow.alignment = { vertical: "middle", horizontal: "center" };
        detailsSheet.mergeCells(`A${detailRow}:E${detailRow}`);
        titleRow.height = 24;
        detailRow++;

        let prevWasBlank = false;
        const writeBlank = () => {
            if (prevWasBlank) return;
            detailRow++;
            prevWasBlank = true;
        };

        for (const rawLine of primary.debug) {
            const item = parseDetailLine(rawLine);
            if (item === null) {
                writeBlank();
                continue;
            }

            // Breathing room before headings (h2 gets an extra blank so top-level
            // sections stand apart visually).
            const isHeading = item.level === "h2" || item.level === "h3" || item.level === "h4";
            if (isHeading && detailRow > 2) {
                writeBlank();
                if (item.level === "h2") writeBlank();
            }

            const r = detailsSheet.getRow(detailRow);
            const cell = r.getCell(item.indent + 1);
            cell.value = item.text;
            applyDetailStyle(cell, item.level);
            if (item.indent < NUM_COLS - 1) {
                const colLetter = (idx: number) => String.fromCharCode("A".charCodeAt(0) + idx);
                detailsSheet.mergeCells(`${colLetter(item.indent)}${detailRow}:${colLetter(NUM_COLS - 1)}${detailRow}`);
            }
            detailRow++;
            prevWasBlank = false;
        }

        detailsSheet.columns = [
            { width: 4 },
            { width: 4 },
            { width: 4 },
            { width: 4 },
            { width: 130 },
        ];
        detailsSheet.views = [{ state: "frozen", ySplit: 1, xSplit: 0, topLeftCell: "A2" }];
    }

    // Add Recommended Configuration sheet with image
    const image = await captureRecommendationImage();
    if (image) {
        try {
            const imageSheet = wb.addWorksheet("Recommended Configuration");
            const imageId = wb.addImage({
                base64: image.dataUrl.split(",")[1],
                extension: "png",
            });
            imageSheet.addImage(imageId, {
                tl: { col: 0, row: 0 },
                ext: { width: image.width, height: image.height },
            });
        } catch (err) {
            console.warn("Failed to add image sheet to export", err);
        }
    }

    // Write to buffer and save
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    const flavourLabel = DEPLOYMENT_TYPE_LABELS[hlp.deploymentType] || hlp.deploymentType;
    const flavourSlug = flavourLabel
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    saveAs(blob, `${flavourSlug}-sizing-${ts}.xlsx`);
};
